import {ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {BulkEditSession, ExcelFileReadEvent, ExcelFileReadMessage} from '../../../model/bulk-edit-session';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {BulkEditExcelService} from '../../../services/bulk-edit-excel.service';
import {BulkEditChangeTypeEnum} from '../../../model/bulk-edit-change-type.enum';
import {BulkEditChangeRecord} from '../../../model/bulk-edit-change-record';
import {TemplatesService} from '../../../services/templates.service';
import {TemplateComponentContent} from '../../../model/template-component-content';
import {from, of, firstValueFrom} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {BulkEditChangeRecordResult} from '../../../model/bulk-edit-change-record-result';
import {AuditBulkEditTemplateComponentContentsService} from '../../../services/audit-bulk-edit-template-component-contents.service';
import {AuditBulkEditTemplateComponentContent} from '../../../model/audit-bulk-edit-template-component-content';
import {RelevantBulkEditChangesFilterPipe} from '../../../pipes/bulk-edit/relevant-bulk-edit-changes-filter.pipe';
import {Locale} from '../../../model/locale';
import {factory} from '../../../shared/app-logging';
import {ToastrService} from 'ngx-toastr';
import {Product} from '../../../model/product';
import { dragMeta } from 'angular-file/file-upload/ngf.directive';
import { UserService } from '@/app/services/user.service';

const logger = factory.getLogger('TemplateBulkEditImportComponent');

@Component({
  selector: 'app-template-bulk-edit-import',
  templateUrl: './template-bulk-edit-import.component.html',
  styleUrls: ['./template-bulk-edit-import.component.scss']
})
export class TemplateBulkEditImportComponent implements OnInit {
  @Input()
  locales: Locale[];
  @Input()
  localeSel: Locale;
  @Input()
  products: Product[];
  @Input()
  productSel: Product;
  bulkEditSession: BulkEditSession;
  accept = 'application/vnd.ms-excel.sheet.macroenabled.12, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';
  maxSize = 5 * 1024 * 1024;
  validComboDrag: any;
  files: File[] = [];
  currentFiles: File[] = [];
  dragFiles: dragMeta[];
  lastFile: File;
  readingFile: boolean;
  lastInvalids: { file: File, type: string }[];
  alertMessages: Array<ExcelFileReadMessage> = [];
  alertTitle = '';
  steps = [true, false, false];
  uploadingProgress = {requestsCount: 0, responseCount: 0, responses: []};

  @ViewChild('alertModal', { static: true })
  alertModal: ModalDirective;
  @ViewChild('bulkEditPreviewModal')
  bulkEditPreviewModal: ModalDirective;
  @ViewChild('reportBulkEditModal', { static: true })
  reportBulkEditModal: ModalDirective;

  public getOperatorProgramProductDisplayLabel = TemplatesService.getOperatorProgramProductDisplayLabel;
  public breakTag;
  public saveButton = [];

  constructor(private excelService: BulkEditExcelService,
              private templateService: TemplatesService,
              private toastr: ToastrService,
              private auditService: AuditBulkEditTemplateComponentContentsService,
              private userService: UserService,
              private cd: ChangeDetectorRef) {
  }

  ngOnInit() {

    this.excelService.excelFileReadEmitter.subscribe((parsingExcelResult: ExcelFileReadEvent) => {
      if (parsingExcelResult.errors && parsingExcelResult.errors.length > 0) {
        this.readingFile = false;
        this.alertTitle = this.lastFile && this.lastFile.name ? this.lastFile.name : '';
        this.alertMessages = parsingExcelResult.errors;
        this.alertModal.show();
        this.bulkEditSession = new BulkEditSession(null, null,
          null, null, null,
          null, new Date(), new ErrorEvent(''));
      }
      if (!parsingExcelResult.result) {
        return;
      }
      this.toastr.info('The Excel® file was parsed.', 'Read confirmation');
      parsingExcelResult.result.file = this.lastFile;
      this.readingFile = false;
      this.goToNextStep();
      this.bulkEditSession = parsingExcelResult.result;
      const relevantChanges = RelevantBulkEditChangesFilterPipe.filter(this.bulkEditSession.getChangeTypes(), this.bulkEditSession);
      // If the uploaded file changes default content:
      if (this.bulkEditSession.isDefaultCombination() && relevantChanges.length > 0) {
        if (!parsingExcelResult.warnings) {
          parsingExcelResult.warnings = [];
        }
        logger.warn('A file for the English (US) Default content was imported');
        const recordsToRevert = this.bulkEditSession.changes.get(BulkEditChangeTypeEnum.REVERT);
        if (recordsToRevert.length > 0) {
          logger.warn('Attempt to delete default content');
          parsingExcelResult.warnings.unshift(
            new ExcelFileReadMessage('Default content revert attempt',
              'Your file contains ' + recordsToRevert.length +
              ' record' + (recordsToRevert.length > 1 ? 's' : '') + ' to be reverted.' +
              ' The DEFAULT content cannot be reverted and ' + (recordsToRevert.length > 1 ? 'these records' : 'this record') +
              ' will remain UNCHANGED'));
          const unchanged = this.bulkEditSession.changes.get(BulkEditChangeTypeEnum.UNCHANGED);
          this.bulkEditSession.changes.set(BulkEditChangeTypeEnum.UNCHANGED, unchanged.concat(recordsToRevert));
          this.bulkEditSession.changes.set(BulkEditChangeTypeEnum.REVERT, []);
        }
        parsingExcelResult.warnings.unshift(
          new ExcelFileReadMessage('Default content Bulk Edition',
            'WARNING: You have uploaded a file for the English (US) Default content. If you proceed with the bulk edition, ' +
            'you will change the default content for ALL locales and ALL products.'));
      }
      if (parsingExcelResult.warnings && parsingExcelResult.warnings.length > 0) {
        this.alertTitle = this.lastFile && this.lastFile.name ? this.lastFile.name : '';
        this.alertMessages = parsingExcelResult.warnings;
        this.alertModal.show();
      }
    }, error => {
      this.readingFile = false;
      const filename = this.lastFile && this.lastFile.name ? this.lastFile.name : '';
      error.message = `While parsing the import file${filename ? ': ' + filename : ''}. ${error.message}`;
      throw error;
    });
  }


  comboSelectDragNDropInvalidFilesChange(event: any) {
    if (this.lastInvalids && this.lastInvalids.length > 0) {
      this.alertTitle = 'The following file' + (this.lastInvalids.length > 1 ? 's' : '') + ' will be ignored';
      for (const invalidFile of this.lastInvalids) {
        logger.warn(`Invalid file (${invalidFile.file.name}) type: ${invalidFile.type}`);
        this.alertMessages.push(new ExcelFileReadMessage(invalidFile.file.name, (invalidFile.type === 'accept' ? 'Invalid file type' : 'Max size exceeded')));
      }
      this.alertModal.show();
    }
    this.lastInvalids = [];
  }

  comboSelectDragNDropFilesChange(event: any) {
    // The user tries to drag and drop several files
    if (event && (event.length - this.currentFiles.length > 1)) {
      this.files = Object.assign([], this.currentFiles);
      this.alertTitle = 'Try again';
      this.alertMessages.push(new ExcelFileReadMessage('Invalid operation', 'Please, drop only one file at once'));
      this.alertModal.show();
    } else if (this.currentFiles.length === 0) {
      this.currentFiles = Object.assign([], this.files);
    } else {
      this.currentFiles = [this.files[this.files.length - 1]];
      this.files = Object.assign([], this.currentFiles);
    }
  }

  goToStep(idx) {
    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i] = i === idx;
    }
  }

  goToPreviousStep() {
    for (let i = 0; i < this.steps.length; i++) {
      if (this.steps[i] && i > 0) {
        this.steps[i] = false;
        this.steps[i - 1] = true;
        break;
      }
    }
    this.saveButton = [];
  }

  goToNextStep() {
    for (let i = 0; i < this.steps.length; i++) {
      if (this.steps[i] && i < (this.steps.length - 1)) {
        this.steps[i] = false;
        this.steps[i + 1] = true;
        break;
      }
    }
  }

  uploadBulkEditExcelVBAFile(f: File) {
    this.readingFile = true;
    this.excelService.readBulkEditExcelFile(f);
  }

  removeFileAt(index: number) {
    this.files.splice(index, 1);
    this.currentFiles.splice(index, 1);
  }

  emptyFileQueue() {
    this.files = [];
    this.currentFiles = [];
    this.lastFile = undefined;
  }

  changeTypeSummaryText(type: BulkEditChangeTypeEnum): string {
    let result = 'Records to ' + type;
    switch (type) {
      case BulkEditChangeTypeEnum.UNCHANGED:
        result = type + ' records';
        break;
      default:
        break;
    }
    return result;
  }

  gatherRequestObservables(ber: BulkEditChangeRecord, idx) {
    let result;
    switch (ber.type) {
      case BulkEditChangeTypeEnum.MODIFY:
      case BulkEditChangeTypeEnum.ADD:
        result = this.templateService.saveTemplateComponentContent(ber.newContent);
        break;
      case BulkEditChangeTypeEnum.REVERT:
        result = this.templateService.removeTemplateComponentContent(ber.currentContent.id);
        break;
      case BulkEditChangeTypeEnum.UNCHANGED:
        break;
      default:
        break;
    }
    result = result.pipe(catchError(err => of(err)));
    return result;
  }

  responseRequestMatching(outVal: BulkEditChangeRecord, inVal: TemplateComponentContent | Error, outIdx: number, inIdx: number) {
    outVal.result = inVal;
    // inVal can be ERROR or TemplateComponentContent
    if (!(inVal instanceof TemplateComponentContent)) {
      outVal.error = inVal;
    }
    this.uploadingProgress.responses[outIdx] = inVal instanceof TemplateComponentContent;
    return new BulkEditChangeRecordResult(inIdx, outIdx, outVal, inVal);
  }

  async saveChanges() {
    //const user = new User(this.authService.getUserName(), this.authService.getUserName());
    const user = await firstValueFrom(this.userService.fetchUserDetails());
    let audit = AuditBulkEditTemplateComponentContent.create(this.bulkEditSession, user);
    this.auditService.startBulkEditAuditSession(audit).subscribe((auditSession: AuditBulkEditTemplateComponentContent) => {
      logger.info('Bulk edit started.');
      if (!(auditSession && auditSession.id)) {
        this.toastr.warning('The connection to the back end is lost, your changes might not be saved, please upload the file again.', 'An error has occurred');
        throw new Error('While attempting to perform changes during a Bulk edit transaction: The bulk edit session was not persisted.');
      }
      auditSession.username = audit.username;
      auditSession.productName = audit.productName;
      auditSession.userId = audit.userId;
      audit = auditSession;
      const changesToPerform = this.bulkEditSession.getChangesToPerform();
      audit.oldContent = changesToPerform[0].currentText;
      audit.newContent = changesToPerform[0].newContent.content;
      this.uploadingProgress.requestsCount = changesToPerform.length;
      this.uploadingProgress.responses = new Array(this.uploadingProgress.requestsCount);
      this.reportBulkEditModal.show();
      const observablesToPerform$ = from(changesToPerform);
      const bulkRequests$ = observablesToPerform$.pipe(
        mergeMap((ber: BulkEditChangeRecord, berIdx: number) => this.gatherRequestObservables(ber, berIdx).pipe(
          map((resp: TemplateComponentContent | Error, respIdx: number) => this.responseRequestMatching(ber, resp, berIdx, respIdx))
        ), 5));
      bulkRequests$.subscribe((value: BulkEditChangeRecordResult) => {
        logger.debug(`Change performed: ${value}`);
        this.uploadingProgress.responseCount++;
        this.bulkEditSession.result ? this.bulkEditSession.result.push(value) : this.bulkEditSession.result = [value];
        if (changesToPerform.length === this.bulkEditSession.result.length) {
          this.saveChangesFinished(audit);
        }
      }, error => {
        error.message = `While attempting to persist a record during a Bulk edit transaction. ${error.message}`;
        throw error;
      });
    }, error => {
      this.toastr.warning('The connection to the back end is lost, your changes might not be saved, please upload the file again.', 'An error has occurred');
      error.message = `While attempting to perform changes during a Bulk edit transaction. ${error.message}`;
      throw error;
    });
  }

  saveChangesFinished(audit: AuditBulkEditTemplateComponentContent) {
    this.auditService.endBulkEditAuditSession(audit).subscribe((auditSession: AuditBulkEditTemplateComponentContent) => {
      logger.info('Bulk edit successfully finished.');
      this.saveButton = [];
    }, error => {
      this.toastr.warning('The connection to the back end is lost, your changes might not be saved, please upload the file again.', 'An error has occurred');
      error.message = `While attempting to perform changes during a Bulk edit transaction. ${error.message}`;
      throw error;
    });
    this.goToStep(0);
    this.emptyFileQueue();
  }

  endBulkEditSession() {
    this.uploadingProgress = {requestsCount: 0, responseCount: 0, responses: []};
    this.bulkEditSession = undefined;
    this.emptyFileQueue();
    this.resetAlertMessages();
    this.steps = [true, false, false];
  }

  resetAlertMessages() {
    this.alertMessages = [];
    this.alertTitle = '';
  }
  checkBreakTag(emit) {
    // Disabled button if at least one changes does not meet the requirements
    // changes detection to make sure angular lifecycle detects every changes for the @output changes
    this.saveButton.push(emit);
    this.breakTag = this.saveButton.some((event) => event);
    this.cd.detectChanges();
  }
}
